/**
 * Vendor-Customer price list admin endpoints
 * (`/api/admin/prices*`). Settings -> Prices tab.
 *
 * - Platform admins can list / set / end any (vendor, customer, item).
 * - Vendor admins / Vendor Tech can list / set / end rows where
 *   `vendor_company_id == their own`.
 * - Customer admins get read-only on rows where
 *   `customer_company_id == their own`.
 */
import { apiBase, apiFetch } from "./apiClient";

export interface PriceRow {
  vendor_company_id: number;
  customer_company_id: number;
  item_id: number;
  effective_utc: string | null;
  unit_price: number | null;
  currency: string;
  end_utc: string | null;
  created_by: string;
  created_utc: string | null;
  note: string | null;
  vendor_name: string | null;
  customer_name: string | null;
  /** md_items.sku — the vendor's part number. */
  sku: string | null;
  item_name: string | null;
  /** Customer's own part number for this vendor's item. NULL when
   *  not configured; the consignment report falls back to `sku`. */
  customer_part_no: string | null;
}

export interface PricesListResponse {
  prices: PriceRow[];
  can_write: boolean;
  total: number;
  page: number;
  page_size: number;
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, token, init);
}

export function listPrices(
  token: string,
  q: {
    vendor_id?: number;
    customer_id?: number;
    item_id?: number;
    include_ended?: boolean;
    limit?: number;
    offset?: number;
  } = {},
): Promise<PricesListResponse> {
  const params = new URLSearchParams();
  if (q.vendor_id != null) params.set("vendor_id", String(q.vendor_id));
  if (q.customer_id != null) params.set("customer_id", String(q.customer_id));
  if (q.item_id != null) params.set("item_id", String(q.item_id));
  if (q.include_ended) params.set("include_ended", "1");
  if (q.limit != null) params.set("limit", String(q.limit));
  if (q.offset != null) params.set("offset", String(q.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return req(token, `/api/admin/prices${qs}`);
}

export function upsertPrice(
  token: string,
  body: {
    vendor_company_id: number;
    customer_company_id: number;
    item_id: number;
    unit_price: number;
    currency?: string;
    note?: string;
    customer_part_no?: string | null;
  },
): Promise<{
  vendor_company_id: number;
  customer_company_id: number;
  item_id: number;
  unit_price: number;
  currency: string;
  customer_part_no: string | null;
  effective_utc: string;
}> {
  return req(token, "/api/admin/prices", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function endPrice(
  token: string,
  body: { vendor_company_id: number; customer_company_id: number; item_id: number },
): Promise<{ ended: boolean }> {
  return req(token, "/api/admin/prices/end", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---------- Bulk export / import ----------

/** Download the active price list as a CSV Blob (every row across
 *  the catalog, optionally filtered by vendor / customer / item). */
export async function exportPricesCsv(
  token: string,
  q: { vendor_id?: number; customer_id?: number; item_id?: number } = {},
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams();
  if (q.vendor_id != null) params.set("vendor_id", String(q.vendor_id));
  if (q.customer_id != null) params.set("customer_id", String(q.customer_id));
  if (q.item_id != null) params.set("item_id", String(q.item_id));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const r = await fetch(`${apiBase()}/api/admin/prices.csv${qs}`, {
    headers: { Accept: "text/csv", "access-token": token },
    cache: "no-store",
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const j = await r.json();
      if (j?.error) detail = j.error;
    } catch {
      /* fall through */
    }
    throw new Error(detail);
  }
  const disposition = r.headers.get("Content-Disposition") ?? "";
  const m = /filename="?([^";]+)"?/.exec(disposition);
  return {
    blob: await r.blob(),
    filename: m?.[1] ?? "vendor-customer-prices.csv",
  };
}

export interface PriceImportError {
  row_index: number;
  message: string;
}
export interface PriceImportResult {
  created: number;
  errors: PriceImportError[];
  total_input_rows: number;
}

/** Bulk upsert from raw CSV text (the same shape as the export). */
export function importPricesCsv(
  token: string, csv: string,
): Promise<PriceImportResult> {
  return req<PriceImportResult>(token, "/api/admin/prices/import", {
    method: "POST",
    body: JSON.stringify({ csv }),
  });
}

/** Convenience: trigger a Blob download. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
